import React, { useMemo, useState } from "react";
import PopupModules from "src/features/auth/components/PopupModules";
import { LedgerRecord, Order } from "src/shared/services/interface";
import { Button } from "src/business-types/grocery/components/Button";
import InputBox from "../../components/InputBox";

type Props = {
  returnReplace: boolean;
  setReturnReplace: () => void;
  order: Order;
  orderChange?: Order;
  changeConfirm: (data: changeDetails) => void;
  ledgers: LedgerRecord | undefined;
};

export type changeDetails = { difference: number; paymentType: string; amount: number; balance: number; accountAdjesment: string };

function ReturnReplace({ returnReplace, setReturnReplace, order, orderChange, changeConfirm, ledgers }: Props) {
  const [mode, setMode] = useState<"refund" | "credit">("refund");
  const [selectedOption, setSelectedOption] = useState<string>("Cash");
  const [resPrice, setResPrice] = useState<string>("");
  const oldPaid = Number(order?.Finalamount || 0);
  const newPaid = Number(orderChange?.Finalamount || 0);
  const difference = newPaid - oldPaid;

  const isWalkIn = order?.CustomerName === "XXXX";

  // ✅ New Balance
  const newBalance = useMemo(() => {
    const entered = Number(resPrice || 0);
    return ledgers?.total_outstanding_balance ?? 0 + difference - entered;
  }, [resPrice, difference, ledgers]);

  // ✅ Input handler
  const pricePayment = (value: string) => {
    if (!/^\d*(\.\d{0,2})?$/.test(value)) return;
    const amountDifferent = ledgers?.total_outstanding_balance ?? 0 + difference;
    if (Number(value) <= amountDifferent + 0.001) {
      setResPrice(value);
    }
  };

  // ✅ Final amount rule
  const finalAmount =
    isWalkIn && difference > 0
      ? Math.abs(difference) // full payment only
      : Number(resPrice || 0);

  return (
    <PopupModules isOpen={returnReplace} onClose={setReturnReplace} className='w-1/2 max-h-2/3' title='Order Modify Confirmation'>
      <div className='p-4 space-y-4'>
        {/* Customer */}
        <div className='text-lg font-semibold'>{isWalkIn ? "Walk-in Customer" : order.CustomerName}</div>

        {/* Summary */}
        <div className='bg-gray-100 p-3 rounded space-y-2'>
          <div className='flex justify-between'>
            <span>Old Payment</span>
            <span>{oldPaid.toFixed(2)}</span>
          </div>

          <div className='flex justify-between'>
            <span>Modified Payment</span>
            <span>{newPaid.toFixed(2)}</span>
          </div>

          <div className={`flex justify-between font-bold ${difference < 0 ? "text-red-500" : "text-green-600"}`}>
            <span>Difference</span>
            <span>{Math.abs(difference).toFixed(2)}</span>
          </div>
        </div>

        {/* EXTRA PAYMENT */}
        {difference > 0 && (
          <div className='bg-gray-100 p-3 rounded space-y-3'>
            <div className='text-green-600 font-medium text-center'>Extra payment received. Adjust below</div>

            <div className='flex gap-3 flex-wrap justify-center'>
              {["Buy on credit", "Card", "Cash"].map((t) => (
                <div
                  key={t}
                  className={`px-3 py-2 flex gap-2 items-center cursor-pointer border rounded-md ${
                    selectedOption === t ? "bg-blue-100 border-blue-500" : "border-gray-300"
                  }`}
                  onClick={() => setSelectedOption(t)}
                >
                  <input type='radio' checked={selectedOption === t} onChange={() => setSelectedOption(t)} />
                  <span>{t}</span>
                </div>
              ))}
            </div>

            <div className='grid grid-cols-2 gap-y-2 items-center text-sm'>
              <span className='font-medium'>Difference</span>
              <span>{difference.toFixed(2)}</span>

              <span className='font-medium'>Old Balance</span>
              <span>{Number(ledgers?.total_outstanding_balance).toFixed(2)}</span>

              <span className='font-medium'>Enter Payment</span>
              <InputBox
                disabled={selectedOption === "Buy on credit" || isWalkIn}
                value={isWalkIn ? Math.abs(difference).toFixed(2) : resPrice}
                onChange={(e) => pricePayment(e.target.value)}
              />

              <span className='font-medium'>New Balance</span>
              <span>{newBalance.toFixed(2)}</span>
            </div>
          </div>
        )}

        {/* REFUND */}
        {difference < 0 && (
          <div className='space-y-3'>
            <p className='font-medium text-red-500'>Refund Required</p>

            {!isWalkIn && (
              <div className='flex gap-4'>
                <label>
                  <input type='radio' checked={mode === "refund"} onChange={() => setMode("refund")} />
                  Refund (Cash)
                </label>

                <label>
                  <input type='radio' checked={mode === "credit"} onChange={() => setMode("credit")} />
                  Add to Credit
                </label>
              </div>
            )}

            {isWalkIn && <p className='text-sm text-gray-500'>Only refund available for walk-in</p>}
          </div>
        )}

        {/* ACTIONS */}
        <div className='flex justify-end gap-3 pt-4'>
          <Button label='Cancel' variant='cancel' onClick={setReturnReplace} />
          <Button
            label='Confirm'
            onClick={() => {
              changeConfirm({
                difference,
                paymentType: selectedOption,
                amount: finalAmount,
                balance: newBalance,
                accountAdjesment: mode,
              });
              setReturnReplace();
            }}
          />
        </div>
      </div>
    </PopupModules>
  );
}

export default ReturnReplace;
